/**
* The Color class is holder for a rgb color.
* If called with several properties will override in order:
* rgba > rgb > hex > red|green|blue|alpha
* I.e. if both rgb and hex is given the rgb value will be used.
* @param {object} o - color object
* @param {number} o.red - colors rgb red component
* @param {number} o.green - color rgb green component
* @param {number} o.blue - color rgb blue component
* @param {number} o.alpha - color rgba alpha component
* @param {string} o.hex - color as hex string
* @param {Array.<number>} o.rgb - color as rgb array
* @param {Array.<number>} o.rgba - color as rgba array
*
*/
export class Color {
    constructor(o) {
        o = typeof o === 'undefined' ? '{}' : o;
        var red = typeof o.red !== 'undefined' ? o.red : 0;
        var green = typeof o.green !== 'undefined' ? o.green : 0;
        var blue = typeof o.blue !== 'undefined' ? o.blue : 0;
        var alpha = typeof o.alpha !== 'undefined' ? o.alpha : 100;
        var rgb = typeof o.rgb !== 'undefined' ? o.rgb : '';
        var rgba = typeof o.rgba !== 'undefined' ? o.rgba : '';
        var hex = typeof o.hex !== 'undefined' ? o.hex : '';
        var name = typeof o.name !== 'undefined' ? o.name : '';

        if (hex !== '') {
            var hexInRgb = this.hexToRgb(hex);
            red = hexInRgb.red;
            green = hexInRgb.green;
            blue = hexInRgb.blue;
        }

        if (rgb !== '' && rgb.constructor === Array) {
            red = rgb[0];
            green = rgb[1];
            blue = rgb[2];
        }

        if (rgba !== '' && rgb.constructor === Array) {
            red = rgba[0];
            green = rgba[1];
            blue = rgba[2];
            alpha = rgba[3];
        }

        this.red = red;
        this.green = green;
        this.blue = blue;
        this.alpha = alpha;
        this.name = name;
    }

    get hex() {
        return "#" +
            this.rgbComponentToHex(Math.round(this.red)) +
            this.rgbComponentToHex(Math.round(this.green)) +
            this.rgbComponentToHex(Math.round(this.blue));
    }

    get rgb() {
        return this.rgbArray.join(' ');
    }

    get rgbArray() {
        return [
            Math.round(this.red),
            Math.round(this.green),
            Math.round(this.blue)
        ];
    }

    get rgba() {
        return this.rgbaArray.join(' ');
    }

    get rgbaArray() {
        return [
            Math.round(this.red),
            Math.round(this.green),
            Math.round(this.blue),
            Math.round(this.alpha)
        ];
    }

    hexToRgb(hex) {
        // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
        var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = hex.replace(shorthandRegex, function (m, r, g, b) {
            return r + r + g + g + b + b;
        });

        var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            red: parseInt(result[1], 16),
            green: parseInt(result[2], 16),
            blue: parseInt(result[3], 16)
        } : null;
    }

    /**
     * Source: http://stackoverflow.com/a/5624139,  22/5/2017
     * @returns {number} the hex value of given rgb value
     * @param {object} c a rgb component (an array with 3 numbers [RNUM, GNUM, BNUM])
     */
    rgbComponentToHex(c) {
        var hex = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    }

    /**
    * @param {number} shadeFactor number between 0 and 1
    * @returns {object.Color} a shaded Color object
    */
    shade(shadeFactor) {
        var rgbShade = [];
        var r = this.red * (1 - shadeFactor);
        var g = this.green * (1 - shadeFactor);
        var b = this.blue * (1 - shadeFactor);
        r = r < 0 ? 0 : r;
        g = g < 0 ? 0 : g;
        b = b < 0 ? 0 : b;
        rgbShade.push(Math.round(r));
        rgbShade.push(Math.round(g));
        rgbShade.push(Math.round(b));

        return new Color({ rgb: rgbShade });
    }

    /**
    * Many color persons talks about 80% of this or 10% of that.
    * @param {number} percent number between 0 and 100
    * @returns {object.Color} a tinted Color
    */
    shadePercent(percent) {
        return this.shade((100 - percent) / 100);
    }

    /**
    * @param {number} tintFactor number between 0 and 1
    * @returns {object.Color} a tinted Color
    */
    tint(tintFactor) {
        var rgbTint = [];
        var r = this.red + (255 - this.red) * tintFactor;
        var g = this.green + (255 - this.green) * tintFactor;
        var b = this.blue + (255 - this.blue) * tintFactor;
        r = r > 255 ? 255 : r;
        g = g > 255 ? 255 : g;
        b = b > 255 ? 255 : b;
        rgbTint.push(Math.round(r));
        rgbTint.push(Math.round(g));
        rgbTint.push(Math.round(b));

        return new Color({ rgb: rgbTint });
    }

    /**
    * Many color persons talks about 80% of this or 10% of that.
    * @param {number} percent number between 0 and 100
    * @returns {object.Color} a tinted Color
    */
    tintPercent(percent) {
        return this.tint((100 - percent) / 100);
    }
}